import "../css/header.scss";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FiChevronDown, FiHeart, FiMenu, FiSearch, FiShoppingBag, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";

function Header() {
  const { cartCount, wishlist } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [scrolled, setScrolled] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const pageTitles = {
      "/": `${siteConfig.brandName} | ${siteConfig.tagline}`,
      "/shop": `Shop organic denim | ${siteConfig.brandName}`,
      "/about": `About | ${siteConfig.brandName}`,
      "/shipping": `Shipping | ${siteConfig.brandName}`,
      "/returns": `Returns | ${siteConfig.brandName}`,
      "/contact": `Contact | ${siteConfig.brandName}`,
      "/privacy": `Privacy policy | ${siteConfig.brandName}`,
      "/terms-of-sale": `Terms of sale | ${siteConfig.brandName}`,
      "/cookies": `Cookie policy | ${siteConfig.brandName}`,
      "/faqs": `FAQs | ${siteConfig.brandName}`,
      "/policies": `Store policies | ${siteConfig.brandName}`,
      "/orders-payment": `Orders & payment | ${siteConfig.brandName}`,
      "/cancellations": `Order cancellations | ${siteConfig.brandName}`,
      "/delivery-issues": `Parcel problems | ${siteConfig.brandName}`,
      "/limited-stock": `Limited stock | ${siteConfig.brandName}`,
      "/offer-codes": `Offer codes | ${siteConfig.brandName}`,
      "/product-information": `Product information | ${siteConfig.brandName}`,
      "/accessibility": `Accessibility | ${siteConfig.brandName}`,
      "/privacy-choices": `Data rights | ${siteConfig.brandName}`,
      "/terms-of-use": `Website use | ${siteConfig.brandName}`,
      "/intellectual-property": `Intellectual property | ${siteConfig.brandName}`,
      "/product-care": `Product care | ${siteConfig.brandName}`,
      "/complaints": `Complaints & policy updates | ${siteConfig.brandName}`,
      "/contact-policy": `Contact information | ${siteConfig.brandName}`,
      "/payment": `Payment status | ${siteConfig.brandName}`,
      "/cartpage": `Shopping bag | ${siteConfig.brandName}`,
    };
    if (location.pathname.startsWith("/shopDetails/")) {
      const productId = Number(location.pathname.split("/").pop());
      const product = productData.find((item) => item.id === productId);
      document.title = product ? `${product.name} | ${siteConfig.brandName}` : `Product | ${siteConfig.brandName}`;
    } else if (location.pathname !== "/checkout") {
      document.title = pageTitles[location.pathname] || siteConfig.brandName;
    }
  }, [location.pathname]);

  useEffect(() => {
    const drawerOpen = menuOpen || searchOpen;
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, searchOpen]);

  const suggestions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return productData.slice(0, 4);
    return productData
      .filter((product) =>
        [product.name, product.category, product.attributes.fit, ...product.tags]
          .join(" ")
          .toLowerCase()
          .includes(term)
      )
      .slice(0, 4);
  }, [searchTerm]);

  const submitSearch = (event) => {
    event.preventDefault();
    const value = searchTerm.trim();
    navigate(value ? `/shop?search=${encodeURIComponent(value)}` : "/shop");
  };

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const transparent = isHome && !scrolled && !menuOpen && !searchOpen;

  return (
    <>
      <header className={`site-header ${transparent ? "site-header--transparent" : "site-header--solid"}`}>
        <div className="site-header__left">
          <Link className="site-header__logo" to="/" onClick={scrollTop} aria-label={`${siteConfig.brandName} home`}>
            <span aria-hidden="true"><i>N</i>{siteConfig.logoText.slice(1)}</span>
          </Link>

          <nav className="site-header__nav" aria-label="Primary navigation">
            {siteConfig.navigation.slice(0, 4).map((item) => (
              <NavLink
                key={item.label}
                className={({ isActive }) =>
                  `site-header__pill ${isActive && item.to === "/shop" ? "is-active" : ""}`
                }
                to={item.to}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="site-header__more">
              <button className="site-header__pill" type="button">More <FiChevronDown /></button>
              <div className="site-header__more-menu">
                <Link to="/policies">All policies</Link>
                <Link to="/privacy">Privacy policy</Link>
                <Link to="/cookies">Cookie policy</Link>
                <Link to="/faqs">FAQs</Link>
                <Link to="/terms-of-sale">Terms of sale</Link>
              </div>
            </div>
          </nav>
        </div>

        <div className="site-header__right">
          <button className="site-header__utility site-header__search-button" onClick={() => setSearchOpen(true)} aria-label="Search products">
            <FiSearch aria-hidden="true" />
            <span>Search</span>
          </button>
          <Link className="site-header__utility site-header__favorite-button" to="/shop?wishlist=true" aria-label={`Wishlist with ${wishlist.length} items`}>
            <FiHeart aria-hidden="true" />
            <span className="site-header__desktop-label">Favorites</span>
            {wishlist.length > 0 && <sup>{wishlist.length}</sup>}
          </Link>
          <Link className="site-header__utility site-header__cart-button" to="/cartpage" aria-label={`Shopping bag with ${cartCount} items`}>
            <FiShoppingBag aria-hidden="true" />
            <span className="site-header__desktop-label">Bag</span>
            {cartCount > 0 && <sup>{cartCount}</sup>}
          </Link>
          <button className="site-header__utility site-header__menu-button" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <FiMenu aria-hidden="true" />
            <span>Menu</span>
          </button>
        </div>
      </header>

      <div className={`site-header__backdrop ${menuOpen || searchOpen ? "is-visible" : ""}`} onClick={() => { setMenuOpen(false); setSearchOpen(false); }} aria-hidden="true" />

      <aside className={`nav-drawer ${menuOpen ? "is-open" : ""}`} aria-hidden={!menuOpen} aria-label="Menu">
        <div className="nav-drawer__header">
          <span>{siteConfig.logoText}</span>
          <button onClick={() => setMenuOpen(false)} aria-label="Close menu"><FiX /></button>
        </div>
        <nav className="nav-drawer__main">
          {siteConfig.navigation.map((item, index) => (
            <Link key={item.label} to={item.to} className="nav-drawer__feature-link">
              <span>0{index + 1}</span>{item.label}
            </Link>
          ))}
        </nav>
        <div className="nav-drawer__secondary">
          <Link to="/about">Repair & reuse</Link>
          <Link to="/shipping">Shipping</Link>
          <Link to="/returns">Returns</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/policies">All policies</Link>
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/cookies">Cookie policy</Link>
          <Link to="/faqs">FAQs</Link>
        </div>
        <p>{siteConfig.description}</p>
      </aside>

      <aside className={`search-drawer ${searchOpen ? "is-open" : ""}`} aria-hidden={!searchOpen} aria-label="Product search">
        <div className="search-drawer__header">
          <h2>Search</h2>
          <button onClick={() => setSearchOpen(false)} aria-label="Close search"><FiX /></button>
        </div>
        <form className="search-drawer__form" onSubmit={submitSearch} role="search">
          <FiSearch aria-hidden="true" />
          <input
            autoFocus={searchOpen}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Jeans, jackets, fits..."
            aria-label="Search the collection"
          />
          <button type="submit">View all</button>
        </form>
        <div className="search-drawer__results">
          <div className="search-drawer__label">
            <span>{searchTerm ? "Matching products" : "Popular now"}</span>
            <span>{suggestions.length}</span>
          </div>
          {suggestions.length ? suggestions.map((product) => (
            <Link className="search-result" key={product.id} to={`/shopDetails/${product.id}`}>
              <img src={product.image} alt="" />
              <span className="search-result__copy">
                <strong>{product.name}</strong>
                <small>{product.category} · {product.attributes.fit} fit</small>
              </span>
              <span>{formatPrice(product.price)}</span>
            </Link>
          )) : (
            <p className="search-drawer__empty">Nothing matched that search. Try “indigo” or “wide”.</p>
          )}
        </div>
      </aside>
    </>
  );
}

export default Header;
